import { useState, useEffect, useCallback } from "react";
import { DesktopLayout } from "../../components/DesktopLayout";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { Plus, X, ChevronLeft, ChevronRight, Trash2, Sun, Moon } from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";

const TURNOS = {
  diurno:  { label: "Diurno",  bg: "#FFFBEB", border: "#FDE68A", text: "#92400E", dot: "#F59E0B", icon: Sun },
  noturno: { label: "Noturno", bg: "#EFF6FF", border: "#BFDBFE", text: "#1D4ED8", dot: "#3B82F6", icon: Moon },
};

const diasSemana = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function adicionarDias(base, n) {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function fmtDiaMes(d) {
  return new Date(d + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function fmtSemana(d) {
  return diasSemana[new Date(d + "T12:00:00").getDay()];
}

function isHoje(d) {
  return d === isoDate(new Date());
}

export default function Escala() {
  const { user } = useApp();
  const nomeUsuario = user?.usuarioNome || user?.nome || "Usuário";

  const [offsetSemana, setOffsetSemana] = useState(0);
  const [escala,       setEscala]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [modalAberto,  setModalAberto]  = useState(false);
  const [diaModal,     setDiaModal]     = useState(null);
  const [form,         setForm]         = useState({ vigilante_nome: "", turno: "diurno", observacao: "" });
  const [saving,       setSaving]       = useState(false);
  const [deletando,    setDeletando]    = useState(null);

  const hoje   = new Date(); hoje.setHours(0, 0, 0, 0);
  const inicio = adicionarDias(hoje, offsetSemana * 7);
  const dias   = Array.from({ length: 7 }, (_, i) => isoDate(adicionarDias(inicio, i)));

  const carregar = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("liberty_escala")
      .select("*")
      .eq("empresa_id", EMPRESA_ID)
      .gte("data", dias[0])
      .lte("data", dias[6])
      .order("data")
      .order("turno");
    setEscala(data || []);
    setLoading(false);
  }, [offsetSemana]); // eslint-disable-line

  useEffect(() => { carregar(); }, [carregar]);

  const abrirModal = (dia) => {
    setDiaModal(dia);
    setForm({ vigilante_nome: "", turno: "diurno", observacao: "" });
    setModalAberto(true);
  };

  const fecharModal = () => { setModalAberto(false); setDiaModal(null); };

  const salvar = async () => {
    if (!form.vigilante_nome.trim()) return;
    setSaving(true);
    await supabase.from("liberty_escala").insert({
      empresa_id: EMPRESA_ID,
      data: diaModal,
      turno: form.turno,
      vigilante_nome: form.vigilante_nome.trim(),
      observacao: form.observacao.trim() || null,
      criado_por: nomeUsuario,
    });
    setSaving(false);
    fecharModal();
    carregar();
  };

  const excluir = async (id) => {
    setDeletando(id);
    await supabase.from("liberty_escala").delete().eq("id", id);
    setDeletando(null);
    carregar();
  };

  const porDia = (dia, turno) =>
    escala.filter((e) => e.data === dia && e.turno === turno);

  const totalVigilantes = new Set(escala.map((e) => e.vigilante_nome)).size;

  return (
    <DesktopLayout title="Escala" subtitle="Escala de 7 dias da equipe de vigilância">
      {/* Header controles */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setOffsetSemana((o) => o - 1)}
            className="w-9 h-9 rounded-xl flex items-center justify-center border border-white/10 bg-white/5 hover:bg-white/10 transition"
          >
            <ChevronLeft className="w-4 h-4 text-slate-300" />
          </button>
          <span className="text-sm text-slate-300 font-medium min-w-[200px] text-center">
            {new Date(dias[0] + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })}
            {" – "}
            {new Date(dias[6] + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })}
          </span>
          <button
            type="button"
            onClick={() => setOffsetSemana((o) => o + 1)}
            className="w-9 h-9 rounded-xl flex items-center justify-center border border-white/10 bg-white/5 hover:bg-white/10 transition"
          >
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </button>
          {offsetSemana !== 0 && (
            <button
              type="button"
              onClick={() => setOffsetSemana(0)}
              className="px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-xs text-blue-300 hover:bg-blue-500/30 transition ml-1"
            >
              Hoje
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {totalVigilantes > 0 && (
            <p className="text-sm text-slate-400">
              <span className="font-semibold text-white">{totalVigilantes}</span> vigilante{totalVigilantes !== 1 ? "s" : ""} escalado{totalVigilantes !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </div>

      {/* Grade 7 dias */}
      <div className="grid grid-cols-7 gap-3">
        {dias.map((dia) => (
          <div key={dia} className="flex flex-col gap-2">
            {/* Cabeçalho do dia */}
            <div
              className="rounded-xl p-2.5 text-center"
              style={
                isHoje(dia)
                  ? { background: "rgba(59,130,246,0.15)", border: "1px solid rgba(59,130,246,0.35)" }
                  : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }
              }
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: isHoje(dia) ? "#93c5fd" : "#64748b" }}>
                {fmtSemana(dia)}
              </p>
              <p className="text-[15px] font-bold mt-0.5" style={{ color: isHoje(dia) ? "#bfdbfe" : "#e2e8f0" }}>
                {new Date(dia + "T12:00:00").getDate()}
              </p>
              <p className="text-[10px]" style={{ color: "#475569" }}>
                {new Date(dia + "T12:00:00").toLocaleDateString("pt-BR", { month: "short" })}
              </p>
            </div>

            {/* Turno Diurno */}
            <TurnoColuna
              turno="diurno"
              itens={loading ? null : porDia(dia, "diurno")}
              onExcluir={excluir}
              deletando={deletando}
            />

            {/* Turno Noturno */}
            <TurnoColuna
              turno="noturno"
              itens={loading ? null : porDia(dia, "noturno")}
              onExcluir={excluir}
              deletando={deletando}
            />

            {/* Botão adicionar */}
            <button
              type="button"
              onClick={() => abrirModal(dia)}
              className="w-full py-2 rounded-xl flex items-center justify-center gap-1 text-[11px] font-semibold transition hover:bg-white/10"
              style={{ border: "1px dashed rgba(255,255,255,0.12)", color: "#475569" }}
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
          </div>
        ))}
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-4 mt-6 px-1">
        {Object.entries(TURNOS).map(([key, cfg]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: cfg.dot }} />
            <span className="text-xs text-slate-500">{cfg.label}</span>
          </div>
        ))}
        <span className="text-xs text-slate-600">· clique em "Add" para escalar</span>
      </div>

      {/* Modal */}
      {modalAberto && (
        <div
          className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={fecharModal}
        >
          <div
            className="w-full max-w-sm rounded-2xl overflow-hidden"
            style={{ background: "#0f1117", border: "1px solid rgba(255,255,255,0.1)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <div>
                <p className="font-bold text-white text-[15px]">Adicionar à escala</p>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  {diaModal && `${fmtSemana(diaModal)}, ${fmtDiaMes(diaModal)}`}
                </p>
              </div>
              <button type="button" onClick={fecharModal} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              {/* Turno */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-2 text-slate-500">Turno</label>
                <div className="flex gap-2">
                  {Object.entries(TURNOS).map(([key, cfg]) => {
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, turno: key }))}
                        className="flex-1 py-2.5 rounded-xl flex items-center justify-center gap-1.5 text-[13px] font-semibold transition"
                        style={
                          form.turno === key
                            ? { background: cfg.bg, border: `2px solid ${cfg.dot}`, color: cfg.text }
                            : { background: "rgba(255,255,255,0.05)", border: "2px solid transparent", color: "#64748b" }
                        }
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {cfg.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Nome vigilante */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5 text-slate-500">Vigilante *</label>
                <input
                  type="text"
                  value={form.vigilante_nome}
                  onChange={(e) => setForm((f) => ({ ...f, vigilante_nome: e.target.value }))}
                  placeholder="Nome do vigilante"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl text-[14px] text-white placeholder:text-slate-600 focus:outline-none"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
                />
              </div>

              {/* Observação */}
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5 text-slate-500">Observação (opcional)</label>
                <input
                  type="text"
                  value={form.observacao}
                  onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))}
                  placeholder="Ex: folga compensatória"
                  className="w-full px-4 py-3 rounded-xl text-[14px] text-white placeholder:text-slate-600 focus:outline-none"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}
                />
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={fecharModal}
                  className="flex-none px-5 py-3 rounded-xl text-sm font-semibold text-slate-400"
                  style={{ background: "rgba(255,255,255,0.06)" }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={salvar}
                  disabled={saving || !form.vigilante_nome.trim()}
                  className="flex-1 py-3 rounded-xl text-white font-bold text-sm transition disabled:opacity-40"
                  style={{ background: "#3b82f6" }}
                >
                  {saving ? "Salvando…" : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DesktopLayout>
  );
}

function TurnoColuna({ turno, itens, onExcluir, deletando }) {
  const cfg = TURNOS[turno];
  const Icon = cfg.icon;

  if (itens === null) {
    return (
      <div className="rounded-xl p-2 animate-pulse" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", minHeight: 56 }} />
    );
  }

  return (
    <div
      className="rounded-xl p-2 flex flex-col gap-1.5"
      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", minHeight: 56 }}
    >
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className="w-3 h-3" style={{ color: cfg.dot }} />
        <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: cfg.dot }}>{cfg.label}</span>
      </div>
      {itens.length === 0 ? (
        <p className="text-[10px] text-slate-700 italic">—</p>
      ) : (
        itens.map((item) => (
          <div key={item.id} className="flex items-center justify-between gap-1 group">
            <p className="text-[11px] font-medium text-slate-300 leading-tight truncate">{item.vigilante_nome}</p>
            <button
              type="button"
              onClick={() => onExcluir(item.id)}
              disabled={deletando === item.id}
              className="w-4 h-4 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition text-red-400 hover:bg-red-500/20 flex-shrink-0"
            >
              {deletando === item.id ? (
                <span className="w-2 h-2 rounded-full border border-red-400 border-t-transparent animate-spin" />
              ) : (
                <X className="w-2.5 h-2.5" />
              )}
            </button>
          </div>
        ))
      )}
    </div>
  );
}
